import { Injectable } from '@angular/core';
import { ChatAiConfig } from '@mitel-internal/cloudlink-components-chat';
import { environment } from '../../environments/environment';
import { MITEL_AVATAR_URI } from '../classes/mitel-avatar';
import { Subject } from 'rxjs';

const READY_MESSAGE = 'cl-chat:ready';
const SET_ASSISTANT_AVATAR_MESSAGE = 'cl-chat:set-assistant-avatar';
const SET_AI_OPTIONS_MESSAGE = 'cl-chat:set-ai-options';
const NEW_CHAT_MESSAGE = 'cl-chat:new-chat';
export const HANDOFF_MESSAGE = 'cl-chat:handoff';

// cl-chat:set-ai-options payload - each field is optional and only replaces what it names; null
// clears greeting/handoff. See DEMO-MAIS-AI-SETUP.md.
export interface AiOptionsMessage {
  greeting?: { text: string; options?: string[] } | null;
  handoff?: { label: string } | null;
  quickReplies?: boolean;
}

// Shared between ChatPageComponent (which passes this to <mi-chat [aiConfig]>) and the
// Options page (which lets the user edit it) - a single source of truth instead of each
// duplicating sessionStorage read/write logic.
@Injectable({ providedIn: 'root' })
export class AiConfigService {
  private readonly STORAGE_KEY = 'clChatDemoAiCredentials';

  config: ChatAiConfig = {
    baseUrl: environment.maisApiBaseUrl,
    authToken: '',
    workspaceSlug: '',
    mode: 'query',
    assistantUserId: 'ai-assistant',
    assistantAvatarUrl: MITEL_AVATAR_URI,
    // Demo example content only - the library itself ships no greeting or handoff defaults.
    // quickReplies only does anything once the workspace prompt asks for the ```ui block (see
    // DEMO-MAIS-AI-SETUP.md); without it, replies render exactly as before.
    quickReplies: true,
    greeting: {
      text: "Hi, I'm your AI assistant. Ask me anything about the documents in this workspace.",
      options: ['What can you help with?', 'Summarize the latest updates']
    },
    handoff: { label: 'Talk to a person' }
  };

  // Fires whenever config is replaced from outside the Options page (currently only
  // listenForParentMessages()) - ChatPageComponent is OnPush in a zoneless app, so it has to be
  // told to re-read the aiConfig getter.
  readonly configChanged$ = new Subject<void>();
  // An iframe parent asked for a fresh AI session (cl-chat:new-chat) - ChatPageComponent owns the
  // <mi-chat> instance, so it's the one that calls startNewAiSession().
  readonly newChatRequested$ = new Subject<void>();

  get isEmbeddedInFrame(): boolean {
    return window.parent !== window;
  }

  constructor() {
    this.restore();
    this.applyQueryParamOverrides();
    this.listenForParentMessages();
  }

  // Replaces config rather than mutating it - <mi-chat [aiConfig]> only runs ngOnChanges (where
  // the library re-applies the assistant's avatar) when the object reference changes.
  setAssistantAvatarUrl(url: string): void {
    this.config = { ...this.config, assistantAvatarUrl: url };
    this.configChanged$.next();
  }

  // Same replace-not-mutate rule as setAssistantAvatarUrl(). Used by the Options page, an iframe
  // parent's cl-chat:set-ai-options and restore() alike.
  setAiOptions(options: AiOptionsMessage): void {
    this.config = this.mergeAiOptions(this.config, options);
    this.configChanged$.next();
  }

  // Anything malformed is ignored field by field rather than rejecting the whole payload, since
  // each field stands on its own - and a field that's absent altogether leaves the current value.
  private mergeAiOptions(config: ChatAiConfig, options: AiOptionsMessage): ChatAiConfig {
    const next: ChatAiConfig = { ...config };
    if (options.greeting === null) {
      delete next.greeting;
    } else if (typeof options.greeting?.text === 'string') {
      const starterOptions = Array.isArray(options.greeting.options)
        ? options.greeting.options.filter((o): o is string => typeof o === 'string')
        : undefined;
      next.greeting = { text: options.greeting.text, options: starterOptions };
    }
    if (options.handoff === null) {
      delete next.handoff;
    } else if (typeof options.handoff?.label === 'string') {
      next.handoff = { label: options.handoff.label };
    }
    if (typeof options.quickReplies === 'boolean') {
      next.quickReplies = options.quickReplies;
    }
    return next;
  }

  // Outbound counterpart to listenForParentMessages(). '*' because this app doesn't know its
  // parent's origin up front; which origins may frame it at all is restricted by CSP
  // frame-ancestors, and nothing posted this way is a secret (a sessionId is useless without the
  // workspace API key).
  notifyParent(message: { type: string; [key: string]: unknown }): void {
    if (this.isEmbeddedInFrame) {
      window.parent.postMessage(message, '*');
    }
  }

  // Lets an iframe parent push the assistant avatar and AI options in after load, and start a new
  // chat, instead of packing everything into the URL (see DEMO-MAIS-AI-SETUP.md for the message
  // shapes). Only messages from window.parent itself are accepted - which origins may frame this
  // app at all is already restricted by CSP frame-ancestors, so no separate origin allowlist is
  // kept here.
  private listenForParentMessages(): void {
    if (!this.isEmbeddedInFrame) {
      return;
    }
    window.addEventListener('message', (event: MessageEvent) => {
      if (event.source !== window.parent) {
        return;
      }
      switch (event.data?.type) {
        case SET_ASSISTANT_AVATAR_MESSAGE: {
          const url = event.data.url;
          if (typeof url === 'string' && /^(data:image\/|https?:\/\/)/i.test(url)) {
            this.setAssistantAvatarUrl(url);
          }
          break;
        }
        case SET_AI_OPTIONS_MESSAGE:
          this.setAiOptions(event.data);
          break;
        case NEW_CHAT_MESSAGE:
          this.newChatRequested$.next();
          break;
      }
    });
    // '*' is safe here: the message carries no data, it only tells the parent it can send now.
    window.parent.postMessage({ type: READY_MESSAGE }, '*');
  }

  // Query params take priority over sessionStorage (restore(), above) - lets an iframe embedder
  // supply MAIS options directly on the URL (e.g. ?embed=true&baseUrl=...&workspaceSlug=...&
  // authToken=...&mode=...) instead of requiring the user to visit /options first. Read straight
  // off window.location.hash rather than via ActivatedRoute/Router: this service is constructed
  // as soon as it's first injected, and Router's own query-param state isn't guaranteed to be
  // synced yet at that point (see AppComponent.initialQueryParams for the same race elsewhere).
  private applyQueryParamOverrides(): void {
    const queryString = window.location.hash.split('?')[1];
    if (!queryString) {
      return;
    }
    const params = new URLSearchParams(queryString);
    const baseUrl = params.get('baseUrl');
    const workspaceSlug = params.get('workspaceSlug');
    const authToken = params.get('authToken');
    const mode = params.get('mode');
    if (baseUrl) {
      this.config.baseUrl = baseUrl;
    }
    if (workspaceSlug) {
      this.config.workspaceSlug = workspaceSlug;
    }
    if (authToken) {
      this.config.authToken = authToken;
    }
    if (mode === 'chat' || mode === 'query') {
      this.config.mode = mode;
    }
  }

  // Reload wipes this service's in-memory config entirely - without this, workspaceSlug/
  // authToken/baseUrl would have to be retyped every time, even though they rarely change day
  // to day. sessionStorage (not the MAIS_API_BASE_URL DI token CHAT_ASSET_BASE_URL uses) since
  // this needs to be user-adjustable per session, not fixed at app-bootstrap time.
  save(): void {
    sessionStorage.setItem(
      this.STORAGE_KEY,
      JSON.stringify({
        baseUrl: this.config.baseUrl,
        workspaceSlug: this.config.workspaceSlug,
        authToken: this.config.authToken,
        mode: this.config.mode,
        // null (not omitted) when cleared, so restore() can tell "turned off" apart from "saved
        // before these options existed" and only the latter falls back to the demo defaults.
        quickReplies: !!this.config.quickReplies,
        greeting: this.config.greeting ?? null,
        handoff: this.config.handoff ?? null
      })
    );
  }

  private restore(): void {
    const saved = sessionStorage.getItem(this.STORAGE_KEY);
    if (!saved) {
      return;
    }
    try {
      const { baseUrl, workspaceSlug, authToken, mode, quickReplies, greeting, handoff } = JSON.parse(saved);
      this.config.baseUrl = baseUrl ?? this.config.baseUrl;
      this.config.workspaceSlug = workspaceSlug ?? this.config.workspaceSlug;
      this.config.authToken = authToken ?? this.config.authToken;
      this.config.mode = mode ?? this.config.mode;
      this.config = this.mergeAiOptions(this.config, { quickReplies, greeting, handoff });
    } catch {
      // Ignore malformed/corrupted storage - keep the defaults.
    }
  }
}
